import {
  IonButton,
  IonContent,
  IonIcon,
  IonPage,
  IonTabBar,
  IonTabButton,
  IonLabel,
} from "@ionic/react";
import { openOutline } from "ionicons/icons";
import { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import AuthenticationGate from "../components/AuthenticationGate";
import GroupHeader from "../components/groups/GroupHeader";
import InformationTab from "../components/tabs/InformationTab";
import MembersTab from "../components/tabs/MembersTab";
import SettingsTab from "../components/tabs/SettingsTab";
import PermissionsTab from "../components/tabs/PermissionsTab";
import GroupNotify from "../components/GroupNotify";
import { Spinner, PageError } from "pymble-ui/components";
import { useMyDetails } from "../hooks/useMyDetails";
import { useGroupDetail } from "../hooks/useGroupDetail";
import { PRESETS_ADMIN_ENABLED, portalUrl } from "../config";

import "./GroupDetail.css";

const ADMIN_TABS = [
  { key: "information", label: "Information" },
  { key: "members", label: "Members" },
  { key: "notify", label: "Notify" },
  { key: "settings", label: "Settings" },
  { key: "permissions", label: "Permissions" },
];

type TabKey =
  | "information"
  | "members"
  | "notify"
  | "settings"
  | "permissions";

const StaffGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoading, isError, user } = useMyDetails();

  if (isLoading) return <Spinner message="Checking access..." />;
  if (isError)
    return <Spinner message="Error loading user details. Please refresh." />;
  if (!user?.active?.staff) {
    return (
      <div className="ion-padding ion-text-center">
        <h2>Access Denied</h2>
        <p>This application is available to staff only.</p>
      </div>
    );
  }

  return <>{children}</>;
};

const GroupDetail: React.FC = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const history = useHistory();

  const { group, counts, isLoading, isError } = useGroupDetail(id);
  const { hasAdminAccess } = useMyDetails();

  // Closes group-manager#81: preset-typed groups must NOT mount the legacy
  // group-admin tabs (Settings would overwrite Type.Id="preset" on save;
  // Permissions is meaningless for a preset). When the Presets admin tab is
  // enabled, jump to its detail page; otherwise we leave the user on this
  // page and show a banner so they know why some tabs look wrong - the
  // SettingsTab guard added below is the defence-in-depth against the
  // type clobbering. history.replace not push so the back button doesn't
  // bounce them into a redirect loop.
  const isPresetGroup = group?.Type?.Id === "preset";
  useEffect(() => {
    if (isPresetGroup && PRESETS_ADMIN_ENABLED) {
      history.replace(`/presets/${id}`);
    }
  }, [isPresetGroup, id, history]);

  // User can manage this group if they are a global group admin
  // OR an admin member of this specific group
  const isGroupAdmin =
    hasAdminAccess || group?.Permission?.Admin === true;

  const visibleTabs = ADMIN_TABS;

  const activeTab: TabKey = visibleTabs.some((t) => t.key === tab)
    ? (tab as TabKey)
    : "information";

  const navigateTab = (tabKey: string) => {
    history.replace(`/groups/${id}/${tabKey}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "information":
        return (
          <InformationTab
            description={group?.Description?.Long}
            counts={counts}
            onNavigateTab={navigateTab}
          />
        );
      case "members":
        return <MembersTab groupId={id} isAdmin={isGroupAdmin} />;
      case "notify":
        return <GroupNotify groups={[id]} />;
      case "settings":
        return group ? (
          <SettingsTab groupId={id} group={group} />
        ) : null;
      case "permissions":
        return group ? (
          <PermissionsTab groupId={id} group={group} />
        ) : null;
      default:
        return (
          <InformationTab
            description={group?.Description?.Long}
            counts={counts}
            onNavigateTab={navigateTab}
          />
        );
    }
  };

  return (
    <IonPage>
      <AppHeader />
      <IonContent fullscreen>
        <AuthenticationGate>
          <StaffGate>
            {isLoading ? (
              <Spinner message="Loading group..." />
            ) : isError || !group ? (
              <PageError error="Could not load group details." />
            ) : isPresetGroup && PRESETS_ADMIN_ENABLED ? (
              <div className="ion-padding ion-text-center">
                <p>
                  This group is a preset; opening it in the Presets tab...
                </p>
              </div>
            ) : !isGroupAdmin ? (
              <div className="ion-padding ion-text-center">
                <h2>Admin access required</h2>
                <p>
                  You need admin access to this group to manage it here. You can
                  still view the group in My Pymble.
                </p>
                <IonButton
                  fill="outline"
                  color="secondary"
                  href={`${portalUrl}/groups/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View in My Pymble
                  <IonIcon slot="end" icon={openOutline} />
                </IonButton>
              </div>
            ) : (
              <div className="group-detail-container">
                <GroupHeader
                  group={group}
                  memberCount={counts.members}
                  isAdmin={isGroupAdmin}
                />

                <div className="group-detail-body">
                  <div className="group-tabs-container">
                    <IonTabBar className="group-tab-bar">
                      {visibleTabs.map((t) => (
                        <IonTabButton
                          key={t.key}
                          tab={t.key}
                          onClick={() => navigateTab(t.key)}
                          selected={activeTab === t.key}
                          className={
                            activeTab === t.key ? "tab-selected" : ""
                          }
                        >
                          <IonLabel>{t.label}</IonLabel>
                        </IonTabButton>
                      ))}
                    </IonTabBar>

                    <div className="group-tab-content">
                      {renderTabContent()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </StaffGate>
        </AuthenticationGate>
      </IonContent>

    </IonPage>
  );
};

export default GroupDetail;
